import { LayoutLoading } from 'components/LayoutLoading'
import { type ReactNode, useEffect, useState } from 'react'
import { getAccountingDashboard } from 'services'
import { svgLoading } from 'stories/assets'
import { thousandSeperator } from 'utils'

type DashboardData = Awaited<ReturnType<typeof getAccountingDashboard>>

const formatCurrency = (value?: number) => {
  if (value == null || (!value && value !== 0)) return '-'
  return `$${thousandSeperator(value)}`
}

const formatCount = (value?: number) => {
  if (value == null) return '-'
  return String(value)
}

const formatPercent = (value?: number) => {
  if (value == null) return '-'
  return `${(Number(value) * 100).toFixed(1)}%`
}

const formatLeverage = (value?: number) => {
  if (value == null) return '-'
  return `${Number(value).toFixed(2)}x`
}

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="mb-6">
    <div className="bg-blue-100 text-blue-900 font-semibold px-3 py-2 text-sm uppercase tracking-wide">{title}</div>
    <div className="border border-t-0 border-gray-200">{children}</div>
  </div>
)

const Row = ({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) => (
  <div
    className={`flex items-center justify-between gap-4 px-3 py-2 text-sm border-b border-gray-100 last:border-b-0 ${
      highlight ? 'bg-green-50 font-semibold' : ''
    }`}
  >
    <span className="text-gray-700">{label}</span>
    <span className="font-medium text-gray-900 whitespace-nowrap">{value}</span>
  </div>
)

export const AccountingDashboard = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<DashboardData | null>(null)

  useEffect(() => {
    setLoading(true)
    getAccountingDashboard()
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [])

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-2 items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center">
          Nexys Mortgage Banking Dashboard
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 animate-spin" />
            </span>
          )}
        </h1>
      </div>

      <p className="text-sm text-gray-600 mb-6">
        Live KPIs across the loan subledger, warehouse, treasury, secondary marketing, hedging, and the GL. Period to
        date{data?.asOfDate ? ` (as of ${data.asOfDate})` : ''}.
      </p>

      <div className="relative">
        <LayoutLoading show={isLoading} />
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <div>
            <Section title="Portfolio">
              <Row label="Loans in inventory (count)" value={formatCount(data?.portfolio?.loansInInventoryCount)} />
              <Row label="Inventory UPB (MLHS + constr.)" value={formatCurrency(data?.portfolio?.inventoryUpb)} />
              <Row label="Pipeline locked (notional)" value={formatCurrency(data?.portfolio?.pipelineLockedNotional)} />
              <Row label="Loans sold to date (count)" value={formatCount(data?.portfolio?.loansSoldToDateCount)} />
            </Section>

            <Section title="Warehouse">
              <Row label="Total commitment" value={formatCurrency(data?.warehouse?.totalCommitment)} />
              <Row label="Total outstanding" value={formatCurrency(data?.warehouse?.totalOutstanding)} />
              <Row label="Total available" value={formatCurrency(data?.warehouse?.totalAvailable)} />
              <Row label="Blended utilization" value={formatPercent(data?.warehouse?.blendedUtilization)} />
            </Section>

            <Section title="Treasury">
              <Row
                label={data?.treasury?.operatingCashLabel || 'Operating cash'}
                value={formatCurrency(data?.treasury?.operatingCash)}
              />
              <Row
                label={`Custodial cash (${data?.treasury?.custodialCashGlAccountNo || '1060'})`}
                value={formatCurrency(data?.treasury?.custodialCash)}
              />
            </Section>

            <Section title="Servicing & Risk">
              <Row
                label={`MSR carrying value (${data?.servicingAndRisk?.msrGlAccountNo || '1500'})`}
                value={formatCurrency(data?.servicingAndRisk?.msrCarryingValue)}
              />
              <Row
                label={`Servicing fee income (${data?.servicingAndRisk?.servicingFeeGlAccountNo || '4060'})`}
                value={formatCurrency(data?.servicingAndRisk?.servicingFeeIncome)}
              />
              <Row
                label={`Repurchase reserve (${data?.servicingAndRisk?.repurchaseReserveGlAccountNo || '2220'})`}
                value={formatCurrency(data?.servicingAndRisk?.repurchaseReserve)}
              />
            </Section>
          </div>

          <div>
            <Section title="Secondary Marketing">
              <Row label="Open sale receivable" value={formatCurrency(data?.secondaryMarketing?.openSaleReceivable)} />
              <Row
                label={`Realized gain on sale (incl. MSR) (${
                  data?.secondaryMarketing?.realizedGainGlAccountNo || '4010'
                })`}
                value={formatCurrency(data?.secondaryMarketing?.realizedGainOnSale)}
              />
              <Row
                label="Origination fee income"
                value={formatCurrency(data?.secondaryMarketing?.originationFeeIncome)}
              />
            </Section>

            <Section title="Hedging & Fair Value">
              <Row label="IRLC fair value (1400)" value={formatCurrency(data?.hedgingAndFairValue?.irlcFairValue)} />
              <Row
                label="Forward / TBA fair value (1410)"
                value={formatCurrency(data?.hedgingAndFairValue?.forwardTbaFairValue)}
              />
              <Row
                label="MLHS fair-value mark (1210)"
                value={formatCurrency(data?.hedgingAndFairValue?.mlhsFairValueMark)}
              />
              <Row label="Pair-off loss (5040)" value={formatCurrency(data?.hedgingAndFairValue?.pairOffLoss)} />
              <Row label="Net hedge FV gain (4070)" value={formatCurrency(data?.hedgingAndFairValue?.netHedgeFvGain)} />
            </Section>

            <Section title="Profit & Loss (period)">
              <Row label="Total revenue" value={formatCurrency(data?.profitAndLoss?.totalRevenue)} />
              <Row label="Total expenses" value={formatCurrency(data?.profitAndLoss?.totalExpenses)} />
              <Row label="NET INCOME" value={formatCurrency(data?.profitAndLoss?.netIncome)} highlight />
            </Section>

            <Section title="Capital & Covenants">
              <Row label="Total assets" value={formatCurrency(data?.capitalAndCovenants?.totalAssets)} />
              <Row
                label="Tangible net worth (equity)"
                value={formatCurrency(data?.capitalAndCovenants?.tangibleNetWorth)}
              />
              <Row
                label="Warehouse leverage (x equity)"
                value={formatLeverage(data?.capitalAndCovenants?.warehouseLeverage)}
              />
              <Row
                label="Reconciliation status"
                value={data?.capitalAndCovenants?.reconciliationStatus || '-'}
                highlight={Boolean(data?.capitalAndCovenants?.allTie)}
              />
            </Section>
          </div>
        </div>
      </div>
    </div>
  )
}
